import moment from 'moment-timezone';
import Model from '@/lib/data/Model';
import Store from '@/store/store';
import SharedEntity from '@/data/SharedEntity';
import Customer from './Customer'; // eslint-disable-line
import Risk from './Risk';
import SurveyInfo from './SurveyInfo';
import SurveyType from './SurveyType';
import VisitForm from './VisitForm';
import VisitRisk from './VisitRisk';
import MicroVisitLink from './MicroVisitLink';
import VisitDocument from './VisitDocument';
import VisitAIRisk from './VisitAIRisk';
import VisitNathanGraph from './VisitNathanGraph';
import ServiceAgreement from './ServiceAgreement';
import Address from './Address';
import RiskImprovement from './RiskImprovement';
import VisitrQaGraph from './VisitQraGraph';

class Visit extends Model {
    static idProperty = 'id';

    constructor(data) {
        super(data);
        this.init();
    }

    fields = [
        {
            name: 'id',
        },
        'visit_code',
        {
            name: 'customer_id',
        },
        {
            name: 'customer_name',
        },
        {
            name: 'customer',
            association: {
                type: 'one',
                model: Customer,
            },
        },
        {
            name: 'legacy_risk',
            association: {
                type: 'one',
                model: Risk,
            },
        },
        {
            name: 'risk',
            association: {
                type: 'one',
                model: VisitRisk,
            },
        },
        {
            name: 'form_data',
            association: {
                type: 'many',
                model: VisitForm,
            },
        },
        {
            name: 'survey_info',
            association: {
                type: 'one',
                model: SurveyInfo,
            },
        },
        {
            name: 'survey_type',
            association: {
                type: 'one',
                model: SurveyType,
            },
        },
        {
            name: 'link',
            association: {
                type: 'one',
                model: MicroVisitLink,
            },
        },
        {
            name: 'representative_id',
        },
        {
            name: 'start_date',
            defaultValue: new Date(),
        },
        {
            name: 'end_date',
            defaultValue: new Date(),
        },
        {
            name: 'start_time',
            defaultValue: new Date(),
        },
        {
            name: 'end_time',
            defaultValue: new Date(),
        },
        {
            name: 'time_zone',
            defaultValue: moment.tz.guess(),
        },
        {
            name: 'record_date_utc',
        },
        {
            name: 'modification_date_utc',
        },
        {
            name: 'record_by_user_id',
        },
        {
            name: 'record_state',
            type: 'int',
            defaultValue: 1,
        },
        {
            name: 'organization_id',
        },
        {
            name: 'status',
            defaultValue: 'pending',
        },
        {
            name: 'description',
        },
        {
            name: 'survey_type_id',
        },
        {
            name: 'copy_visit_id',
        },
        {
            name: 'shared_entity',
            association: {
                type: 'one',
                model: SharedEntity,
            },
        },
        {
            name: 'files',
            association: {
                type: 'many',
                model: VisitDocument,
            },
        },
        {
            name: 'policy_id',
        },
        {
            name: 'address_id',
        },
        {
            name: 'address',
        },
        {
            name: 'policy',
        },
        {
            name: 'industry_name',
        },
        {
            name: 'claim_id',
        },
        {
            name: 'ai_risk',
            association: {
                type: 'one',
                model: VisitAIRisk,
            },
        },
        {
            name: 'nathan_graph',
            association: {
                type: 'one',
                model: VisitNathanGraph,
            },
        },
        {
            name: 'service_agreement_id',
        },
        {
            name: 'service_agreement',
            association: {
                type: 'one',
                model: ServiceAgreement,
            },
        },
        {
            name: 'shared_user_id',
        },
        {
            name: 'address',
            association: {
                type: 'one',
                model: Address,
            },
        },
        {
            name: 'visit_seq',
        },
        {
            name: 'org_tag',
        },
        {
            name: 'visit_seq',
        },
        {
            name: 'riskImprovements',
            association: {
                type: 'many',
                model: RiskImprovement,
                collectionConfig: {
                    url: '/visit/riskimprovements',
                    method: 'GET',
                },
            },
        },
        {
            name: 'qra_graph',
            association: {
                type: 'one',
                model: VisitrQaGraph,
            },
        },
    ];

    get start_date_moment() {
        return moment(this.start_date);
    }

    get start_date_display() {
        return this.start_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.momentDisplayFormat);
    }

    get start_date_time_display() {
        return this.start_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.momentDateTimeDisplayFormat);
    }

    get start_time_display() {
        return this.start_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.timeDisplayFormat);
    }

    get end_date_moment() {
        return moment(this.end_date);
    }

    get end_date_display() {
        return this.end_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.momentDisplayFormat);
    }

    get end_date_time_display() {
        return this.end_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.momentDateTimeDisplayFormat);
    }

    get end_time_display() {
        return this.end_date_moment
            .locale(window.localStorage.language_code)
            .format(Store.getters.timeDisplayFormat);
    }

    get representative() {
        return Store.getters.context.representatives.find(
            (item) => item.id == this.representative_id
        );
    }

    get lat() {
        return this.address ? this.address.latitude : null;
    }

    get lng() {
        return this.address ? this.address.longitude : null;
    }

    get isPending() {
        return this.status == 'pending';
    }

    get isInProgress() {
        return this.status == 'inprogress';
    }

    get isFinalized() {
        return this.status == 'finalized';
    }

    get isInReview() {
        return this.status == 'inreview';
    }

    get isReviewed() {
        return this.status == 'reviewed';
    }

    get isIssued() {
        return this.status == 'issued';
    }

    get isArchived() {
        return this.status == 'archived';
    }

    get score() {
        return this.survey_type.legacy ? this.legacy_risk.total_rank : this.risk.ratio;
    }

    get canDelete() {
        return (
            this.record_by_user_id == Store.getters.user.id &&
            Store.getters.context.canDelete('survey_management_inspection') &&
            this.status != 'finalized' &&
            this.status != 'archived' &&
            this.form_data.length == 0
        );
    }

    /* visite ait organizasyon useri silebilir */
    get canArchive() {
        return (
            this.organization_id == Store.getters.user.organization_id &&
            Store.getters.context.canDelete('survey_management_inspection') &&
            this.status == 'finalized' &&
            this.form_data.length == 0
        );
    }

    get startTimeDisplay2VisitTimeZone() {
        return this.start_date_moment
            .tz(this.time_zone)
            .locale(window.localStorage.language_code)
            .format(Store.getters.timeDisplayFormat);
    }

    get endTimeDisplay2VisitTimeZone() {
        return this.end_date_moment
            .tz(this.time_zone)
            .locale(window.localStorage.language_code)
            .format(Store.getters.timeDisplayFormat);
    }

    get serviceAgreementName() {
        return this.service_agreement?.agreement_name;
    }

    get reviewer() {
        return Store.getters.context.users.find((item) => item.id == this.shared_user_id);
    }

    get visitCanBeEditable() {
        /* pending visit can be editable by the owner RE or privileged admin
        status do not change */
        const visitRE = this.representative_id == Store.getters.user.representative_id;
        const hasPrivilege = Store.getters.context.canUpdate('survey_management_inspection_report');
        return (visitRE || hasPrivilege) && ['pending'].includes(this.status);
    }

    get visitCanBeFillable() {
        /* pending,inprogress,finalized and reviewed
        visit can be editable by the owner RE
        status do not change until finalize */
        const visitRE = this.representative_id == Store.getters.user.representative_id;
        return visitRE && ['pending', 'inprogress', 'finalized', 'reviewed'].includes(this.status);
    }

    get visitCanBeIssued() {
        /* finalized visit can be issued(issued status) if user has privileges (admin??) */
        const hasPrivilege = Store.getters.context.canUpdate('survey_management_issue_survey');
        return hasPrivilege && ['finalized'].includes(this.status);
    }

    get visitCanBeInreview() {
        /* finalized visit can be send to review(inreview status) if user has privileges (admin or owner risk eng.) */
        const hasPrivilege = Store.getters.context.canUpdate('survey_management_inreview_survey');
        return hasPrivilege && ['finalized'].includes(this.status);
    }

    get visitCanBeReviewed() {
        /* inreview status visit can be reviewed
        (reviewed status) if user has privileges and selected as reviewer(admin or any risk eng.) */
        const isReviewer = this.shared_user_id == Store.getters.user.id;
        const hasPrivilege = Store.getters.context.canUpdate('survey_management_reviewed_survey');
        return hasPrivilege && isReviewer && ['inreview'].includes(this.status);
    }

    get visitShowOnlineReport() {
        /* inreview status visit can be reviewed (reviewed status) if user has privileges and selected as reviewer(admin or any risk eng.) */
        const hasPrivilege = Store.getters.context.canRead('survey_management_inspection_report');
        return (
            hasPrivilege && ['inreview', 'reviewed', 'finalized', 'issued'].includes(this.status)
        );
    }

    get visitCanCallStarted() {
        /* ongoing visit can start call ?? */
        return ['pending', 'inprogress', 'inreview', 'reviewed', 'finalized'].includes(this.status);
    }

    api = [
        {
            name: 'save',
            url: '/visit/save',
            params: [
                'id',
                'copy_visit_id',
                'start_date',
                'end_date',
                'time_zone',
                'description',
                'representative_id',
                'customer_id',
                'organization_id',
                'survey_type_id',
                'visit_code',
                'files',
                'policy_id',
                'address_id',
                'service_agreement_id',
            ],
        },
        {
            name: 'saveOnCall',
            url: '/visit/save_on_call',
            params: [
                'customer_name',
                'start_date',
                'end_date',
                'time_zone',
                'description',
                'representative_id',
                'organization_id',
                'survey_type_id',
                'visit_code',
                'files',
            ],
        },
        {
            name: 'delete',
            url: '/visit/delete',
            params: ['id'],
            method: 'DELETE',
        },
        {
            name: 'fetchFormsData',
            url: '/visit/forms/data',
            params: ['id', 'language_code'],
        },
        {
            name: 'finalize',
            url: '/mobile/visit/finalize',
            params: ['id', 'hidden_empty_mandatory_questions'],
        },
        {
            name: 'recalculate',
            url: '/mobile/visit/finalize',
            params: ['id', 'hidden_empty_mandatory_questions'],
        },
        {
            name: 'visitDetail',
            url: '/mobile/visit',
            params: ['id', 'language_code', 'entity_type'],
            method: 'POST',
        },
        {
            name: 'setPolicyId',
            url: '/visit/setPolicyId',
            params: ['id', 'policy_id'],
        },
        {
            name: 'sendToReview',
            url: '/visit/sendtoreview',
            params: ['id'],
        },
        {
            name: 'assign',
            url: '/visit/assign',
            params: [
                'id',
                'end_date',
                'representative_id',
                'customer_id',
                'organization_id',
                'survey_type_id',
                'claim_id',
                'address_id',
            ],
        },
        {
            name: 'archive',
            url: '/visit/archive',
            params: ['id'],
        },
        {
            name: 'shareWithEng',
            url: '/visit/shareWithEng',
            params: ['id', 'user_id'],
        },
        {
            name: 'issueVisit',
            url: '/visit/issue',
            params: ['id'],
        },
        {
            name: 'reviewVisit',
            url: '/visit/review',
            params: ['id'],
        },
    ];
}

export default Visit;
